import platform from 'platform';
import { version } from '../package.json';
import { STANDARD_EVENT_NAME, CURRENT_SESSION_STARTED_AT_STORAGE_KEY } from './constants';
import { TriggeredEvent as EventPayload, TriggeredEvent } from './triggered-event.types';
import { UserProfileType } from './user-profiles/user-profile.types';
import { adjustDateWithOffset, formatDate } from './utility/date';
import { PushNotificationSubscriber } from './push-notification-subscriber';
import { storage } from './utility/storage';
import { console } from './utility/console';
import { getRandInt } from './utility/random';

const OS_LENGTH_MAX = 255; // MySQL の users.os の上限
const OS_VERSION_LENGTH_MAX = 255; // MySQL の users.os_version の上限
const CHUNK_SIZE_LIMIT = 1024 * 32; // sendBeaconの上限サイズ(64kb)だが、body以外の領域 + 3rd PartyのsendBeacon等を考えて半分にしてある

interface UserProfilePayload {
  [key: string]: { type: UserProfileType; value: any };
}

type Chunk = {
  events: EventPayload;
  user: UserProfilePayload;
};

type Payload = { [P in keyof Chunk]: Chunk[P][] };

// TODO: Cope with the new format using a.k.a. Event Tracking API
// ref. https://repro.esa.io/posts/31837/revisions/9/diff
type Metadata = {
  insight_id: number | null;
  session_id: string;
  idfa: string;
  idfv: string;
  width: number;
  height: number;
  network: string;
  user_annotation: string;
  push_token: string;
  ip_address: string;
  app_version: string;
  production: boolean;
  bundle_id: string;
  device: string;
  sdk_version: string;
  platform: string;
  os: string;
  os_version: string;
  push_enabled: boolean;
  web_push_subscription: PushSubscriptionJSON | null;
  extras: Record<string, any>;
};

const defaultMetadata: Metadata = {
  insight_id: null,
  session_id: '',
  idfa: '',
  idfv: '',
  width: 0,
  height: 0,
  network: '',
  user_annotation: '',
  push_token: '',
  ip_address: '',
  app_version: '',
  production: false,
  bundle_id: window.location.host,
  device: navigator.platform,
  sdk_version: version,
  platform: 'web',
  os: platform.os && platform.os.family ? platform.os.family.slice(0, OS_LENGTH_MAX) : 'unknown',
  os_version: platform.os && platform.os.version ? platform.os.version.slice(0, OS_VERSION_LENGTH_MAX) : 'unknown',
  push_enabled: false,
  web_push_subscription: null,
  extras: {
    started_at: '',
    ended_at: '',
    url: '',
    snippet_version: '',
    browser: platform.name,
    browser_version: platform.version,
    use_beacon: true,
    timing: '',
    user_agent: navigator.userAgent,
    tabID: '',
  },
};

type Params = {
  insightId: number;
  idfv: string;
  userAnnotation: string;
  snippetVersion: string;
  timeOffsetMills: number;
  ipAddress: string;
  setupOptions: Record<string, any>;
};

export class EventChunk {
  // upload() をするのに必要なパラメータ群
  // EventChunkUploader の初期化以降保有し続ける immutable なオブジェクト
  // eventChunkの定期アップロードに使用する
  private metadata: Metadata;
  private payload: Payload;
  private started_at: Date;
  private timeOffsetMills: number;
  private snippetVersion: string;

  public constructor({
    insightId,
    idfv,
    userAnnotation,
    snippetVersion,
    timeOffsetMills,
    ipAddress,
    setupOptions = {},
  }: Params) {
    this.timeOffsetMills = timeOffsetMills;
    this.started_at = new Date();
    this.snippetVersion = snippetVersion;

    this.metadata = {
      ...defaultMetadata,
      idfv,
      insight_id: insightId,
      user_annotation: userAnnotation,
      ip_address: ipAddress,
    };

    const normalizedOpts = {
      ...setupOptions,
      linker_domains: setupOptions.linker_domains ? setupOptions.linker_domains.join(',') : '',
      disable_ip_list: setupOptions.disable_ip_list ? setupOptions.disable_ip_list.join(',') : '',
    };

    // あくまでも各サイトにおける setup オプションの利用状況を把握したいだけであり
    // すべての event chunk に setupOptions を付与する必要はなく、コストがかさむのを防ぐためにサンプリングして付与する。
    if (getRandInt(0, 1000) === 0) {
      this.metadata.extras = {
        ...this.metadata.extras,
        ...normalizedOpts,
      };
    }

    this.payload = {
      events: [],
      user: [],
    };
  }

  public isEmpty(): boolean {
    const { payload } = this;

    return !payload.user.length && !payload.events.length && this.metadata.web_push_subscription === null;
  }

  public reset() {
    this.payload = Object.keys(this.payload).reduce((cur, key) => {
      return { ...cur, [key]: [] };
    }, {} as Payload);
    this.metadata.web_push_subscription = null;
    this.started_at = new Date();
  }

  // TODO: add なのか append なのか名前を考える
  public register<Key extends keyof Payload>({
    key,
    chunk,
    isNewSession = false,
  }: {
    key: Key;
    chunk: Chunk[Key];
    isNewSession?: boolean;
  }) {
    this.payload[key].push(chunk as any);

    if (isNewSession) {
      this.started_at = new Date();
    }
  }

  public registerSubscription(subscription: PushSubscriptionJSON) {
    this.metadata.web_push_subscription = subscription;
  }

  public get toJSON() {
    const payload = this.formattedPayload;

    return {
      context: this.formattedMetadata,
      ...payload,
    };
  }

  public *createChunkIteratorAndReset() {
    const chunkIterator = this.createSplitChunkIterator(this.formattedPayload);
    const context = this.formattedMetadata;
    this.reset();
    // createChunkIteratorAndResetの呼び出しを行った時点ですべてのChunkがリセットされる
    // 次のcreateChunkIteratorAndResetの呼び出しで同じeventを二重送信してしまうのを避けるため
    // chunkIteratorとcontextはすでにコピーされているので問題なし
    /* eslint-disable no-restricted-syntax */
    for (const payload of chunkIterator) {
      yield {
        context,
        ...payload,
      };
    }
    /* eslint-enable no-restricted-syntax */
  }

  private get formattedMetadata(): Metadata {
    const sessionStartedAt = parseInt(storage.get(CURRENT_SESSION_STARTED_AT_STORAGE_KEY), 10);
    const { idfv } = this.metadata;
    const session_id = `${idfv}-${sessionStartedAt}`;
    const tabID = (() => {
      if (!('sessionStorage' in window)) {
        return '';
      }
      if (window.sessionStorage.rprTabID) {
        return window.sessionStorage.rprTabID;
      }
      window.sessionStorage.rprTabID = Math.random().toString().slice(2);
      return window.sessionStorage.rprTabID;
    })();

    return {
      ...this.metadata,
      session_id,
      extras: {
        ...this.metadata.extras,
        snippet_version: this.snippetVersion,
        started_at: formatDate(adjustDateWithOffset(this.started_at, this.timeOffsetMills)),
        ended_at: formatDate(adjustDateWithOffset(new Date(), this.timeOffsetMills)),
        url: window.location.href, // NOTE: 不正な eventChunk がアップロードされた際の調査のために入れているので、これを利用した機能改修は控えるように
        tabID,
      },
      height: window.innerHeight,
      width: window.innerWidth,
      // eslint-disable-next-line compat/compat
      network: 'connection' in navigator ? navigator.connection.effectiveType : 'unknown',
      // セッション内で Notification.permission が変わることがありえるのでeventChunk アップロード時に最新の状態を取得する必要がある
      push_enabled: PushNotificationSubscriber.isPermissionGranted,
    };
  }

  private get formattedPayload() {
    const {
      payload,
      payload: { events },
    } = this;
    const sessionStartEvent = events.some((event) => event.name === STANDARD_EVENT_NAME('session_start'));
    const oldestTrackedTime = new Date();

    // 同じeventchunk内にsession_startとsession_startより古いイベントがあれば、
    // session_startのtracked_atを古いイベントよりも前になるように書き換える。
    if (sessionStartEvent) {
      const timesTrackedAt: TriggeredEvent['tracked_at'][] = events
        .map((event) => event.tracked_at)
        .sort((a, b) => (a > b ? 1 : -1));
      if (timesTrackedAt.length) {
        const time = timesTrackedAt[0].getTime();
        oldestTrackedTime.setTime(time - 1);
      }
    }

    return {
      events: events.map((event) => ({
        ...event,
        tracked_at:
          event.name === STANDARD_EVENT_NAME('session_start')
            ? adjustDateWithOffset(oldestTrackedTime, this.timeOffsetMills)
            : adjustDateWithOffset(event.tracked_at, this.timeOffsetMills),
      })),
      user: payload.user.reduce((cur, userProperty) => {
        return { ...cur, ...userProperty };
      }, {}),
    };
  }

  private *createSplitChunkIterator({
    events,
    user,
  }: EventChunk['formattedPayload']): Generator<{ events: EventPayload[]; user: UserProfilePayload }> {
    if (!events.length) {
      return {};
    }
    const eventString = JSON.stringify(events[0]);
    const singleChunkEstimatedSize = eventString.length;
    const estimatedSize = singleChunkEstimatedSize * events.length;
    if (estimatedSize >= CHUNK_SIZE_LIMIT) {
      const estimatedUplodableChunkCount = CHUNK_SIZE_LIMIT / singleChunkEstimatedSize;
      let start = 0;
      let end = estimatedUplodableChunkCount;
      while (start < events.length) {
        const sliced = events.slice(start, end);
        if (JSON.stringify({ user, sliced }).length < CHUNK_SIZE_LIMIT) {
          yield { events: sliced, user };
        } else {
          /* eslint-disable-next-line no-plusplus */
          end--;
          if (end === start) {
            console.info('Single chunk size over CHUNK_SIZE_LIMIT');
            yield { events: sliced, user };
          } else {
            /* eslint-disable-next-line no-continue */
            continue;
          }
        }
        start = end;
        end += estimatedUplodableChunkCount;
      }
    } else {
      yield { events, user };
    }
  }
}
